import {buildRequest, buildUrl} from "../../shared/utils/apiCall";
import {randomInt, xmur3} from "../../shared/utils/random";
import {settingsInitialState} from "../settings/reducer";
import {IApplicationState} from "../index";
import {IHandshake, IHandshakeResponse} from "./types";
import {Language, localeToLanguage} from "../settings/Language";
import merge from "../../shared/utils/merge";
import {delay} from "redux-saga";
import ReactDOM from "react-dom";
import {LoadingScreenComponent} from "../../shared/components/loading/LoadingScreen";
import React from "react";
import {getViewportDimensions} from "../settings/sagas/viewportDimensions";
import {IAggregatedMetric, LogLevel} from "../log/types";
import getGlobalErrorHandler from "../../shared/utils/getGlobalErrorHandler";
import {initializeLocalizationDictionary} from "../../shared/i18n";
import { SDK } from "../../sdk";
import { ISettingsState } from "../settings/types";
import interpolate from "../../shared/utils/interpolate";

const MIN_RETRY_TIMEOUT = 1000;
const MAX_RETRY_TIMEOUT = 60000;

function showLoadingScreen(root: HTMLElement | null, language: Language) {
    const canvas = getViewportDimensions(800);
    const style = {
        width: canvas.width,
        height: canvas.height,
        position: "absolute",
        top: 0,
        left: "50%",
        transform: `scale(${canvas.scaleFactor}) translateX(-50%)`,
        transformOrigin: "top left"
    };

    ReactDOM.render(React.createElement('div', { style }, LoadingScreenComponent({
        isLoading: true,
    })), root);
}

export default async function handshake(root: HTMLElement | null, payload: IHandshake): Promise<Partial<IApplicationState>> {
    const startTime = new Date().getTime();

    const [locale, language] = localeToLanguage(payload.locale);
    const player = {
        id: payload.id,
        signature: payload.signature,
    } as Record<string, any>;
    
    if (payload.name) {
        player.name = payload.name;
    }

    if (payload.picture) {
        player.picture = payload.picture;
    }

    let state = {
        settings: {
            randomSeed: xmur3(payload.id),
            general: {
                uiLanguage: language,
                locale: locale,
            }
        },
        player
    } as Partial<IApplicationState>;

    let timeout = MIN_RETRY_TIMEOUT;
    let currentEndpointIndex = randomInt(100);

    const loadingTimer = window.setTimeout(showLoadingScreen.bind(null, root, language), 500);
    const {
            api: {
                endpoints: apiEndpoints,
                uriTemplate: apiUriTemplate
            }
        } = settingsInitialState;

    const errorHandler = getGlobalErrorHandler("Handshake", payload, {});
    const trackError = (message: string, error?: Error, data?: Record<string, any>) => errorHandler(message, undefined, undefined, undefined, error, data);

    while (true) {
        const url = buildUrl(apiEndpoints[currentEndpointIndex % apiEndpoints.length], apiUriTemplate, {
            section: "Handshake",
            method: "Post",
        });

        const request = buildRequest(payload.signature, {
            culture: payload.locale,
            clientAppVersion: payload.version,
            platform: payload.platform,
            storeVendor: SDK.instance.name,
            tracking: payload.entryPoint?.tracking || undefined
        });

        try {
            const response = await fetch(url, request);

            if (response.ok) {
                const content = await response.json() as IHandshakeResponse;

                if (content.success) {
                    clearTimeout(loadingTimer);

                    initializeLocalizationDictionary(content.data.localization);

                    const endTime = new Date().getTime();

                    const metrics: IAggregatedMetric[] = [];

                    if (content.data.clientSettings && content.data.clientSettings.performanceMetrics && content.data.clientSettings.performanceMetrics.gameInitialization) {
                        metrics.push(...Object.entries(window.metrics)
                            .map(([action, metric]) => ({
                                logLevel: LogLevel.Debug,
                                category: "gameInitialization",
                                action: action,
                                start: metric.start,
                                end: metric.end,
                                duration: metric.end - metric.start
                            }))
                            .filter(metric => metric.duration > 0));

                        metrics.push({
                            logLevel: LogLevel.Debug,
                            category: "gameInitialization",
                            action: "handshake",
                            start: startTime,
                            end: endTime,
                            duration: endTime - startTime
                        });
                    }

                    state = merge(
                        state,
                        content.data.clientData,
                        {
                            log: {
                                metrics: metrics,
                                errors: [],
                            },
                            settings: {
                                appVersion: payload.version,
                                allowDebug: !!content.data.allowDebug,
                                userId: content.data.userId,
                                general: {
                                    uiLanguage: content.data.language,
                                }
                            } as Partial<ISettingsState>
                        } as Partial<IApplicationState>,
                        {
                            settings: content.data.clientSettings
                        } as Partial<IApplicationState>);

                    state = merge(
                        {
                            player: {
                                name: interpolate(state.settings?.general.defaultUserNameTemplate ?? settingsInitialState.general.defaultUserNameTemplate, { userId: content.data.userId }),
                                picture: interpolate(state.settings?.general.defaultUserPictureTemplate ?? settingsInitialState.general.defaultUserPictureTemplate, { userId: content.data.userId }),
                            },
                            settings: {
                                general: {
                                    locale: state.settings?.general?.availableLanguages?.find(lang => lang.value === content.data.language)?.locale ?? 'en-US'
                                }
                            }
                        } as any,
                        state);

                    return state;
                }
            }

            const errorMessage = !response.ok
                ? "Api request failed"
                : "Invalid api response";

            trackError(errorMessage, undefined, {
                url,
                request,
                response,
            });
        } catch (error) {
            trackError("Api request failed", error, {
                url,
                request,
            });
        }

        clearTimeout(loadingTimer);
        showLoadingScreen(root, language);

        await delay(timeout);
        if (timeout < MAX_RETRY_TIMEOUT) {
            timeout = Math.min(MAX_RETRY_TIMEOUT, 2 * timeout);
        }

        currentEndpointIndex++;
    }
}